/**
 * In-memory fixture implementation of RefereeApiClient.
 *
 * Deterministic and stateful per token: transitions mutate a stored context so the resumable
 * step router, guards and each placeholder screen can be driven end-to-end with no backend.
 * Unknown tokens lazily seed to `invited`; a handful of reserved keyword tokens seed straight
 * into a later/terminal state so the edge pages are demoable in dev (open /r/expired, /r/signed…).
 *
 * NB: the person here is Sam Whitmore giving a reference for Jane Doe — the canonical example
 * pair from the design-spec mock, so demos read consistently.
 */
import type {
  AudioInitRequest,
  AudioUploadTicket,
  ConfirmDetailsRequest,
  ConsentAttestation,
  CorrectNameRequest,
  CorrectNameResult,
  DeclineRequest,
  DraftState,
  DraftWrite,
  EditDraftRequest,
  LetterTone,
  ReferenceStatus,
  RefereeContext,
  RegenerateDraftRequest,
  SignDisclosures,
  SignRequest,
  SignResult,
} from './api-types';
import {
  CORRECT_NAME_ERROR_CANNOT_CORRECT,
  CORRECT_NAME_ERROR_INVALID,
  DRAFT_ERROR_CANNOT_REGENERATE,
  DRAFT_ERROR_CAP_REACHED,
  RefereeApiError,
  SIGN_ERROR_ATTESTATION_STALE,
  SIGN_ERROR_CANNOT_SIGN,
  SIGN_ERROR_NAME_MISMATCH,
} from './api-types';
import type { RefereeApiClient, RefereeToken } from './api-client';

/** The server-enforced regeneration cap (`DraftOptions.MaxRegenerations`), mirrored for the demo. */
const MAX_REGENERATIONS = 3;

/**
 * The transcript is ONE string on the wire — no timestamps, no speaker turns. The design mock's
 * "0:04" gutter has no backing data, so the fixture does not invent one either: a fixture that is
 * richer than the wire is how a screen ends up built against data that will never arrive.
 */
const DEMO_TRANSCRIPT =
  'So, I managed Jane for three years at Northwind — she came in as a mid-weight and left running ' +
  'the whole design side.\n\n' +
  'She led the redesign under a really tight deadline. Calm the whole way through, honestly — ' +
  'carried the team when it got rough.\n\n' +
  "I'd work with her again in a heartbeat.";

const DEMO_KEY_POINTS = [
  'Managed Jane for three years at Northwind Studio',
  'Led the redesign under a tight deadline',
  'Calm under pressure — carried the team',
  'Would gladly work with her again',
];

const DEMO_LETTER =
  'To whom it may concern,\n\n' +
  'I managed Jane Doe for three years at Northwind Studio and recommend her without ' +
  'hesitation. She led our redesign under a punishing deadline, stayed calm when it counted, ' +
  'and brought the whole team with her.\n\n' +
  'I would welcome the chance to work with her again.';

/** A visible marker per tone, so a regenerated demo letter is obviously a different one. */
const TONE_PREAMBLE: Record<LetterTone, string> = {
  warm: '(Warmer)',
  professional: '(More formal)',
  concise: '(Shorter)',
};

/** Reserved tokens → seed status, so every edge/terminal page is reachable in dev. */
const SEED_BY_TOKEN: Record<string, ReferenceStatus> = {
  confirm: 'accepted',
  record: 'details_confirmed',
  processing: 'processing',
  review: 'drafted',
  signed: 'signed',
  declined: 'declined',
  expired: 'expired',
  withdrawn: 'withdrawn',
  'under-review': 'needs_review',
  'under-review-bare': 'needs_review',
  rerecord: 'rerecord_requested',
};

/**
 * Tokens whose request has NOTHING extracted — the `no_key_points` divert, where extraction found
 * nothing usable so Sonnet was never called and there is no letter AND no key points anywhere.
 * A `needs_review` fixture that always carries a rich transcript + bullets would let a screen be
 * built against data that this real, tested path never produces.
 */
const BARE_TOKENS = new Set(['under-review-bare']);

function seedContext(token: RefereeToken): RefereeContext {
  const status = SEED_BY_TOKEN[token] ?? 'invited';
  return {
    referenceId: `ref_${simpleHash(token)}`,
    status,
    candidate: {
      name: 'Jane Doe',
      initials: 'JD',
      headline: 'Product designer · Northwind Studio',
      note: "Sam — your word would mean a lot for a design lead role I'm going for. Thank you either way.",
    },
    referee: {
      name: 'Sam Whitmore',
      roleThen: 'Former Manager',
      company: 'Northwind Studio',
      relationship: 'manager',
    },
    maxRecordingSeconds: 180,
    redraftsRemaining: 3,
    declineReasons: ['no_time', 'not_comfortable', 'dont_know_well', 'prefer_not'],
    // The current attestation the consent POST must echo — mirrors what the real `/context` surfaces
    // so the Confirm step's consent-version wiring is exercised end-to-end by the mock seam too.
    consentDocumentKey: 'referee_attestation',
    consentDocumentVersion: '1',
    // NB: the REAL `/context` wire carries neither of these (see referee-wire.ts) — only the sign
    // response does. They are seeded here purely so the reserved `/r/signed` demo token can render
    // the full done screen without signing; production's revisit path has no code to show.
    verificationCode: status === 'signed' ? mockVerificationCode('demo') : undefined,
    signedAt: status === 'signed' ? new Date().toISOString() : undefined,
  };
}

function simpleHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/* ---------------------------------------------------------------------------------------------- *
 * Signing (WP-B15a contract, mirrored)
 * ---------------------------------------------------------------------------------------------- */

/** The attestation version this fixture publishes; a stale echo is a 422, as in production. */
const DEMO_ATTESTATION_VERSION = '1';

/**
 * `SignAttestation.Text`, VERBATIM from the backend constant. Copied rather than paraphrased
 * because it is the exact string the real disclosures endpoint serves, and the sign screen's whole
 * layout question is "does this much text fit calmly above the button" — a shorter stand-in would
 * answer that question wrongly. The SPA never authors this: production reads it off the wire.
 */
const DEMO_ATTESTATION_TEXT =
  'I confirm that this reference is my own words, that it is true to the best of my knowledge, ' +
  'and that I intend my typed name to be my signature on it.\n\n' +
  'By My Word confirmed my contact details and my possession of the link sent to me. It did not ' +
  'verify my identity by any other means, and this reference is signed and published on that basis.';

/** Crockford's Base32 alphabet — digits + uppercase minus I, L, O and U (`VerificationCodeFactory`). */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * A verification code in the REAL canonical shape: 24 Crockford-Base32 characters, uppercase, no
 * separators.
 *
 * Deliberately not the old `BMW-7Q2K` fixture. That placeholder was 8 characters where production
 * mints 24, and "does a 24-character unbroken code wrap sanely on a 320px phone" is a real layout
 * question the done screen has to answer — one a short fixture silently answers for you, wrongly.
 * Same principle as this file's transcript note: a fixture that is unlike the wire is how a screen
 * gets built against data that will never arrive.
 *
 * Deterministic per token (not a CSPRNG) — this is a demo fixture, and stable output keeps the
 * screen re-renderable and the demos readable. Nothing here is security-bearing.
 */
function mockVerificationCode(seed: string): string {
  let h = 0;
  let out = '';
  for (let i = 0; i < 24; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i % seed.length) + i * 7) | 0;
    out += CROCKFORD[(h >>> 0) % CROCKFORD.length];
  }
  return out;
}

/**
 * The server's name comparison, mirrored exactly (`SignService.NameMatches`): trim, collapse every
 * internal whitespace run to one space, compare case-insensitively — and fold nothing else. No
 * accent folding, no spelling tolerance, no dropping of middle names or titles.
 */
function nameMatches(typed: string, signer: string): boolean {
  return collapseName(typed).toLowerCase() === collapseName(signer).toLowerCase();
}

/** Trim + collapse every whitespace run (newlines included) to one space. */
function collapseName(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

/**
 * Is `name` still somewhere in `letterBody`? The mock's mirror of the backend's
 * `SignerNameRule.AppearsIn` (WP-B15c).
 *
 * The BODY is collapsed too, not just the name — a sign-off puts a newline between the two words
 * ("Sincerely,\nSam Whitmore"), which is the single likeliest place a referee's name actually
 * appears, and searching the raw text would miss exactly that. A blank name appears in nothing:
 * `includes('')` is true for every string.
 */
function nameAppearsIn(letterBody: string, name: string): boolean {
  const needle = collapseName(name).toLowerCase();
  return needle.length > 0 && collapseName(letterBody).toLowerCase().includes(needle);
}

/**
 * The referee-facing draft state for a request status — the mock's mirror of the backend's
 * `PollableStates` map. `failed` folds into `processing` exactly as the real service does: it is an
 * operational holding state the sweeps re-drive, so "still working on it" is the honest answer.
 */
function draftStatusFor(status: ReferenceStatus): DraftState['status'] {
  switch (status) {
    case 'needs_review':
      return 'needs_review';
    case 'rerecord_requested':
      return 'rerecord_requested';
    case 'drafted':
      return 'drafted';
    default:
      return 'processing';
  }
}

interface DraftFixture {
  /** How far the pipeline has got: 0 = nothing yet, 1 = transcript, 2 = + key points. */
  beat: number;
  letterBody: string;
  regenerationsUsed: number;
  version: number;
}

function draftFrom(token: RefereeToken, ctx: RefereeContext, fixture: DraftFixture): DraftState {
  const status = draftStatusFor(ctx.status);
  const drafted = status === 'drafted';
  // A `no_key_points` divert has neither key points nor a letter — the drafter was never called.
  const bare = BARE_TOKENS.has(token);
  const beat = drafted ? 2 : fixture.beat;

  return {
    status,
    // The poll returns these AS THEY BECOME READY, which is what makes the processing screen's
    // progressive reveal real rather than a timer pretending to be one.
    keyPoints: bare ? [] : beat >= 2 ? DEMO_KEY_POINTS : [],
    transcript: bare ? undefined : beat >= 1 ? DEMO_TRANSCRIPT : undefined,
    // The letter rides on the STATE, not on row existence: a `needs_review` request's letter stays
    // withheld even when one exists, because that divert is precisely what a human must clear.
    letterBody: drafted ? fixture.letterBody : undefined,
    version: drafted ? fixture.version : undefined,
    regenerationsUsed: fixture.regenerationsUsed,
    regenerationsRemaining: Math.max(0, MAX_REGENERATIONS - fixture.regenerationsUsed),
  };
}

export interface MockClientOptions {
  /** Artificial latency per call (ms). 0 in tests; a small value in dev feels real. */
  latencyMs?: number;
}

export function createMockClient(opts: MockClientOptions = {}): RefereeApiClient {
  const { latencyMs = 0 } = opts;
  const store = new Map<RefereeToken, RefereeContext>();
  // The letter + counters live outside the context so re-polling doesn't clobber a referee's edits
  // and the cap survives across calls (the real cap is derived from the version chain server-side).
  const fixtures = new Map<RefereeToken, DraftFixture>();

  function fixture(token: RefereeToken): DraftFixture {
    let f = fixtures.get(token);
    if (!f) {
      f = { beat: 0, letterBody: DEMO_LETTER, regenerationsUsed: 0, version: 1 };
      fixtures.set(token, f);
    }
    return f;
  }

  const wait = () => (latencyMs > 0 ? new Promise<void>((r) => setTimeout(r, latencyMs)) : Promise.resolve());

  function get(token: RefereeToken): RefereeContext {
    let ctx = store.get(token);
    if (!ctx) {
      ctx = seedContext(token);
      store.set(token, ctx);
    }
    return ctx;
  }

  function set(token: RefereeToken, next: RefereeContext): RefereeContext {
    store.set(token, next);
    return next;
  }

  return {
    async getContext(token) {
      await wait();
      return { ...get(token) };
    },

    async accept(token) {
      await wait();
      const ctx = get(token);
      if (ctx.status === 'invited') return set(token, { ...ctx, status: 'accepted' });
      return { ...ctx };
    },

    async decline(token, _req?: DeclineRequest) {
      await wait();
      const ctx = get(token);
      return set(token, { ...ctx, status: 'declined' });
    },

    async confirmDetails(token, req: ConfirmDetailsRequest) {
      await wait();
      const ctx = get(token);
      return set(token, {
        ...ctx,
        status: ctx.status === 'accepted' ? 'details_confirmed' : ctx.status,
        referee: { ...ctx.referee, ...req },
      });
    },

    async recordConsent(_token: RefereeToken, _consent?: ConsentAttestation) {
      // The offline fixture accepts the attestation (key + version) but has nothing to persist; the
      // real backend appends the acceptance and validates the version.
      await wait();
    },

    async createRealtimeSession() {
      // The offline fixture has no WebRTC provider. The recorder treats null as a quiet fallback
      // and otherwise remains fully demoable.
      await wait();
      return null;
    },

    async extractLiveKeyPoints(_token, req) {
      await wait();
      return req.content
        .split(/[.!?]\s+/)
        .map((part) => part.trim())
        .filter((part) => part.split(/\s+/).length >= 4)
        .slice(-4);
    },

    async initAudioUpload(token, req: AudioInitRequest): Promise<AudioUploadTicket> {
      await wait();
      return {
        uploadId: `upl_${simpleHash(token + req.contentLength)}`,
        url: 'https://files.bymyword.com/mock-presigned-put',
        method: 'PUT',
        headers: { 'Content-Type': req.contentType, 'Content-Length': String(req.contentLength) },
      };
    },

    async uploadAudio(_ticket: AudioUploadTicket, _blob: Blob) {
      // Fake upload path — no network, so the record step (and the whole flow) stays demoable
      // offline. The real single-PUT to R2 lives in the HTTP client.
      await wait();
    },

    async completeAudioUpload(token) {
      await wait();
      const ctx = get(token);
      return set(token, { ...ctx, status: 'processing' });
    },

    async pollDraft(token): Promise<DraftState> {
      await wait();
      const ctx = get(token);
      const f = fixture(token);

      // Walk the real pipeline's beats rather than snapping straight to `drafted`: transcription
      // lands first, then extraction, then the letter. Three polls, so the processing screen's
      // progressive reveal is exercised against a source that reveals — not a fixture that is
      // already complete on the first read.
      if (ctx.status === 'processing' || ctx.status === 'recorded') {
        if (f.beat < 2) {
          fixtures.set(token, { ...f, beat: f.beat + 1 });
        } else {
          set(token, { ...ctx, status: 'drafted' });
        }
      }

      return draftFrom(token, get(token), fixture(token));
    },

    async editDraft(token, req: EditDraftRequest): Promise<DraftWrite> {
      await wait();
      const ctx = get(token);
      // Only a drafted request has a letter to edit.
      if (ctx.status !== 'drafted') {
        throw new RefereeApiError('Cannot edit', 'conflict', 409, 'cannot_edit');
      }
      const f = fixture(token);
      const body = req.letterBody.trim();
      if (body.length === 0) {
        throw new RefereeApiError('Invalid body', 'server', 400, 'invalid_body');
      }
      // An edit that changes nothing appends nothing (the screen saves on blur, so an unchanged
      // body is the ordinary case). Either way an edit is FREE — the counter is untouched.
      const changed = body !== f.letterBody;
      const next: DraftFixture = { ...f, letterBody: body, version: changed ? f.version + 1 : f.version };
      fixtures.set(token, next);
      return {
        letterBody: next.letterBody,
        version: next.version,
        regenerationsUsed: next.regenerationsUsed,
        regenerationsRemaining: Math.max(0, MAX_REGENERATIONS - next.regenerationsUsed),
      };
    },

    async regenerateDraft(token, req: RegenerateDraftRequest): Promise<DraftWrite> {
      await wait();
      const ctx = get(token);
      if (ctx.status !== 'drafted') {
        throw new RefereeApiError('Cannot regenerate', 'conflict', 409, DRAFT_ERROR_CANNOT_REGENERATE);
      }
      const f = fixture(token);
      // The cap is a 409, not a 429 — waiting will never help. Carries the distinct code so the
      // screen can say "button spent, editing still live" rather than "letter frozen, leave".
      if (f.regenerationsUsed >= MAX_REGENERATIONS) {
        throw new RefereeApiError('Regeneration cap reached', 'conflict', 409, DRAFT_ERROR_CAP_REACHED);
      }
      const used = f.regenerationsUsed + 1;
      const next: DraftFixture = {
        ...f,
        letterBody: `${TONE_PREAMBLE[req.tone]}\n\n${DEMO_LETTER}`,
        regenerationsUsed: used,
        version: f.version + 1,
      };
      fixtures.set(token, next);
      return {
        letterBody: next.letterBody,
        version: next.version,
        regenerationsUsed: used,
        regenerationsRemaining: Math.max(0, MAX_REGENERATIONS - used),
      };
    },

    async getSignDisclosures(token): Promise<SignDisclosures> {
      await wait();
      const ctx = get(token);
      // Mirrors the server: only a `drafted` request has anything to disclose, and everything else
      // is one opaque `cannot_sign` — there is no sign screen to render for a request that can't
      // be signed, so the refusal happens before the ceremony draws rather than at its last step.
      if (ctx.status !== 'drafted') {
        throw new RefereeApiError('Cannot sign', 'conflict', 409, SIGN_ERROR_CANNOT_SIGN);
      }
      return {
        signerName: ctx.referee.name,
        attestationText: DEMO_ATTESTATION_TEXT,
        documentKey: 'referee_attestation',
        documentVersion: DEMO_ATTESTATION_VERSION,
      };
    },

    async sign(token, req: SignRequest): Promise<SignResult> {
      await wait();
      const ctx = get(token);

      // Idempotent, exactly as the backend is: a double-tap, a retried POST or a refreshed done
      // screen gets the SAME code back with a success — never a second signature and never a
      // conflict. Modelled here because a fixture that 409s where production 200s would have this
      // screen built to handle an error that cannot happen, and miss the one that can.
      if (ctx.status === 'signed' && ctx.verificationCode && ctx.signedAt) {
        return { verificationCode: ctx.verificationCode, signedAt: ctx.signedAt };
      }
      if (ctx.status !== 'drafted') {
        throw new RefereeApiError('Cannot sign', 'conflict', 409, SIGN_ERROR_CANNOT_SIGN);
      }
      if (req.documentKey !== 'referee_attestation' || req.documentVersion !== DEMO_ATTESTATION_VERSION) {
        throw new RefereeApiError(
          'Attestation version is stale',
          'server',
          422,
          SIGN_ERROR_ATTESTATION_STALE,
        );
      }
      // The real comparison, not a lenient stand-in: trim, collapse internal whitespace runs, fold
      // case — and fold NOTHING else. "Dr Jane Smith" and "Jane R Smith" really do fail against a
      // confirmed "Jane Smith", so the demo and the tests must fail them too. A mock that accepted
      // them would hide the exact trap the retype flow exists for.
      if (!nameMatches(req.typedName, ctx.referee.name)) {
        throw new RefereeApiError('Name mismatch', 'server', 422, SIGN_ERROR_NAME_MISMATCH);
      }

      const signedAt = new Date().toISOString();
      const verificationCode = mockVerificationCode(token);
      set(token, { ...ctx, status: 'signed', signedAt, verificationCode });
      return { verificationCode, signedAt };
    },

    async correctName(token, req: CorrectNameRequest): Promise<CorrectNameResult> {
      await wait();
      const ctx = get(token);

      const fullName = req.fullName.trim();
      if (fullName.length === 0) {
        throw new RefereeApiError('Name required', 'server', 400, CORRECT_NAME_ERROR_INVALID);
      }

      // Pre-sign only, and `signed` lands here like any other unsignable status — deliberately NOT
      // the sign's idempotent-200 posture. The snapshot is immutable, so a post-sign correction
      // cannot be honoured; modelling it as a success would build this screen against an answer
      // production never gives.
      if (ctx.status !== 'drafted') {
        throw new RefereeApiError('Cannot correct name', 'conflict', 409, CORRECT_NAME_ERROR_CANNOT_CORRECT);
      }

      const previousName = ctx.referee.name;

      // Compared ORDINALLY, not with nameMatches: that rule answers "would this typed name pass
      // the ceremony?" and forgives case, but the PDF prints the name verbatim — so "grace hopper"
      // → "Grace Hopper" is a real correction the lenient rule would silently discard.
      if (previousName.trim() === fullName) {
        return { signerName: previousName, previousNameInLetter: false };
      }

      // Asked of the letter as it stands, BEFORE the write, about the name being superseded.
      const previousNameInLetter = nameAppearsIn(fixture(token).letterBody, previousName);

      // The name changes; the LETTER never does. Warn, never rewrite — those are the referee's own
      // words, and the draft editor is open to them at this exact status.
      set(token, { ...ctx, referee: { ...ctx.referee, name: fullName } });

      return { signerName: fullName, previousNameInLetter };
    },
  };
}
